import { useState } from "react";
import { AlertTriangle, Checkbox, Input, Tags } from "../../ui";
import { pairText } from "./copilotModel";

const ALLERGEN_CODES = ["gluten", "dairy", "eggs", "tree_nuts", "peanuts", "soy", "fish", "crustaceans", "molluscs", "sesame", "mustard", "celery", "sulphites", "lupin"];

function Provenance({ value, verified, t }) {
  return (
    <span className="copilot-provenance">
      <Tags variant={value?.edited ? "primary" : "info"} size="sm">
        {value?.edited ? t.userEdited : t.aiSuggestion}
      </Tags>
      <Tags variant={verified ? "success" : "warning"} size="sm">
        {verified ? t.userVerified : t.needsVerification}
      </Tags>
    </span>
  );
}

function CaloriesRow({ row, value, t, lang, editable, busy, verified, onEdit }) {
  const [draft, setDraft] = useState(value?.caloriesKcal ?? "");
  const commit = () => {
    if (String(draft) !== String(value?.caloriesKcal ?? "") && Number(draft) > 0) onEdit({ caloriesKcal: Number(draft) });
  };
  return (
    <li className="copilot-verify-row">
      <div className="copilot-row-main">
        <span className="copilot-row-name" dir="auto">
          {pairText(row.name, lang)}
          {row.context && <small> · {pairText(row.context, lang)}</small>}
        </span>
        <Provenance value={value} verified={verified} t={t} />
      </div>
      <small>
        {t.aiSuggestion}: ~{value?.caloriesKcal ?? "—"} {t.kcal}
      </small>
      {editable && <Input label={t.fields.calories} type="number" min={1} value={draft} disabled={busy} onChange={(e) => setDraft(e.target.value)} onBlur={commit} />}
    </li>
  );
}

function AllergenRow({ row, value, t, lang, editable, busy, verified, onEdit }) {
  const likely = value?.allergens?.likely || [];
  const possible = value?.allergens?.possible || [];
  const toggle = (code) => {
    const next = likely.includes(code) ? likely.filter((c) => c !== code) : [...likely, code];
    onEdit({ allergens: { likely: next, possible: possible.filter((c) => !next.includes(c)) } });
  };
  return (
    <li className="copilot-verify-row">
      <div className="copilot-row-main">
        <span className="copilot-row-name" dir="auto">
          {pairText(row.name, lang)}
          {row.context && <small> · {pairText(row.context, lang)}</small>}
        </span>
        <Provenance value={value} verified={verified} t={t} />
      </div>
      <fieldset className="copilot-allergens" disabled={!editable || busy}>
        <legend>{t.likelyAllergens}</legend>
        {ALLERGEN_CODES.map((code) => (
          <Checkbox key={code} checked={likely.includes(code)} onChange={() => toggle(code)} label={t.allergens[code]} />
        ))}
      </fieldset>
      {possible.length > 0 && (
        <small>
          {t.possibleAllergens}: {possible.map((c) => t.allergens[c] || c).join(lang === "ar" ? "، " : ", ")}
        </small>
      )}
    </li>
  );
}

// AI calorie / allergen estimates: shown as suggestions, editable, and only
// applied after this explicit, per-operation verification. Chat messages
// can never tick this box.
export default function CopilotVerification({ operation, t, lang, editable, busy, verified, onVerify, onEditValues }) {
  const field = operation.params?.contentField;
  const Row = field === "allergens" ? AllergenRow : CaloriesRow;
  const generated = operation.generated || {};
  return (
    <section className="copilot-verify" aria-label={t.needsVerification}>
      <p className="copilot-verify-why">
        <AlertTriangle size={14} aria-hidden="true" /> {t.verifyWhy}
      </p>
      <ul className="copilot-rows">
        {(operation.preview.affected || []).map((row) => (
          <Row key={`${row.id}-${JSON.stringify(generated[row.id] || null)}`} row={row} value={generated[row.id]} t={t} lang={lang} editable={editable} busy={busy} verified={verified} onEdit={(fields) => onEditValues({ [row.id]: fields })} />
        ))}
      </ul>
      {field === "allergens" && <p className="copilot-verify-warning">⚠ {t.verifyAllergenWarning}</p>}
      {editable && <Checkbox checked={verified} onChange={(e) => onVerify(e.target.checked)} label={field === "allergens" ? t.verifyAllergens : t.verifyCalories} />}
    </section>
  );
}
